import { Fragment, useEffect, useMemo, useState } from "react";

import { H2, Body } from "@leafygreen-ui/typography";
import { Select, Option, OptionGroup } from "@leafygreen-ui/select";
import Button from "@leafygreen-ui/button";
import Banner from "@leafygreen-ui/banner";
import Copyable from "@leafygreen-ui/copyable";
import { css } from "@leafygreen-ui/emotion";
import { palette } from "@leafygreen-ui/palette";

import { useApi } from "../providers/Api";
import { getUTCDateForLocalMidnight } from "../utils/passkeyDate";

const spaced = css`
  margin-top: 16px;
`;

const formGroup = css`
  margin-top: 16px;
  max-width: 440px;
`;

const pageTitle = css`
  font-size: 44px;
  line-height: 1.1;
`;

// Highlighted result panel: green-tinted background + border to set the passkey
// apart from the form, matching the "success" treatment in the source generator.
// Copyable renders its label and value (a <code>) as direct children here (it uses
// a Fragment, no wrapper node), so we size them from this parent. The tripled
// ampersand raises specificity so these win over LeafyGreen's built-in styles.
const resultPanel = css`
  margin-top: 24px;
  max-width: 440px;
  padding: 20px;
  border-radius: 8px;
  background-color: ${palette.green.light3};
  border: 2px solid ${palette.green.base};

  &&& label {
    font-size: 18px;
    display: block;
  }
  /* The value + copy button live in a grid wrapper div, a sibling of the label.
     Space that box away from the label. */
  &&& > div {
    margin-top: 10px;
  }
  &&& code {
    font-size: 26px;
    line-height: 1.3;
    letter-spacing: 2px;
  }
`;

const dateLabel = css`
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: ${palette.black};
  font-size: 13px;
`;

const dateInput = css`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid ${palette.gray.base};
  border-radius: 6px;
  background-color: ${palette.white};
  color: ${palette.black};
  &:focus {
    outline: none;
    border-color: ${palette.green.dark1};
    box-shadow: 0 0 0 3px ${palette.green.light2};
  }
`;

// Timezone options ported from the source passkey.html generator. Grouped by region.
// NOTE: the picked date is local to this timezone, but the passkey hash is always
// computed against the UTC calendar date (see api/lib/passkey.mjs and the reference
// lambda). handleGenerate converts local midnight on the picked date to its UTC
// calendar date via getUTCDateForLocalMidnight before calling the API.
const TIMEZONE_GROUPS = [
  {
    label: "Asia Pacific",
    zones: [
      ["Pacific/Auckland", "New Zealand Time (NZT, UTC+12/13)"],
      ["Australia/Sydney", "Australian Eastern Time (AEST/AEDT, UTC+10/11)"],
      ["Australia/Brisbane", "Australia Brisbane (AEST, UTC+10)"],
      ["Australia/Perth", "Australian Western Time (AWST, UTC+8)"],
      ["Asia/Seoul", "Korea Standard Time (KST, UTC+9)"],
      ["Asia/Tokyo", "Japan Standard Time (JST, UTC+9)"],
      ["Asia/Singapore", "Singapore Time (SGT, UTC+8)"],
      ["Asia/Hong_Kong", "Hong Kong Time (HKT, UTC+8)"],
      ["Asia/Shanghai", "China Standard Time (CST, UTC+8)"],
      ["Asia/Taipei", "Taipei Standard Time (UTC+8)"],
      ["Asia/Bangkok", "Indochina Time (ICT, UTC+7)"],
      ["Asia/Ho_Chi_Minh", "Vietnam Time (UTC+7)"],
      ["Asia/Jakarta", "Western Indonesia Time (WIB, UTC+7)"],
      ["Asia/Kolkata", "India Standard Time (IST, UTC+5:30)"],
      ["Asia/Karachi", "Pakistan Standard Time (PKT, UTC+5)"],
    ],
  },
  {
    label: "Middle East",
    zones: [
      ["Asia/Dubai", "Gulf Standard Time (GST, UTC+4)"],
      ["Asia/Tehran", "Iran Standard Time (IRST, UTC+3:30)"],
      ["Asia/Riyadh", "Arabia Standard Time (AST, UTC+3)"],
      ["Asia/Kuwait", "Kuwait Time (UTC+3)"],
      ["Asia/Jerusalem", "Israel Time (IST/IDT, UTC+2/3)"],
      ["Asia/Beirut", "Eastern European / Beirut (UTC+2/3)"],
    ],
  },
  {
    label: "Europe",
    zones: [
      ["UTC", "UTC"],
      ["Europe/London", "UK Time (GMT/BST, UTC+0/1)"],
      ["Europe/Lisbon", "Western European Time (WET/WEST, UTC+0/1)"],
      ["Europe/Paris", "Central European Time (CET/CEST, UTC+1/2)"],
      ["Europe/Berlin", "Germany / Central Europe (CET/CEST, UTC+1/2)"],
      ["Europe/Amsterdam", "Netherlands (CET/CEST, UTC+1/2)"],
      ["Europe/Stockholm", "Sweden / Scandinavia (CET/CEST, UTC+1/2)"],
      ["Europe/Warsaw", "Poland / Central-East Europe (CET/CEST, UTC+1/2)"],
      ["Europe/Helsinki", "Eastern European Time (EET/EEST, UTC+2/3)"],
      ["Europe/Istanbul", "Turkey Time (TRT, UTC+3)"],
      ["Europe/Moscow", "Moscow Standard Time (MSK, UTC+3)"],
    ],
  },
  {
    label: "Africa",
    zones: [
      ["Africa/Casablanca", "Morocco Time (WET/WEST, UTC+0/1)"],
      ["Africa/Lagos", "West Africa Time (WAT, UTC+1)"],
      ["Africa/Accra", "Ghana / West Africa (GMT, UTC+0)"],
      ["Africa/Cairo", "Egypt / Eastern Africa (EET, UTC+2)"],
      ["Africa/Johannesburg", "South Africa Standard Time (SAST, UTC+2)"],
      ["Africa/Nairobi", "East Africa Time (EAT, UTC+3)"],
      ["Africa/Addis_Ababa", "Ethiopia / East Africa (EAT, UTC+3)"],
    ],
  },
  {
    label: "Americas",
    zones: [
      ["America/New_York", "Eastern Time (ET, UTC-5/-4)"],
      ["America/Chicago", "Central Time (CT, UTC-6/-5)"],
      ["America/Denver", "Mountain Time (MT, UTC-7/-6)"],
      ["America/Los_Angeles", "Pacific Time (PT, UTC-8/-7)"],
      ["America/Anchorage", "Alaska Time (AKT, UTC-9/-8)"],
      ["Pacific/Honolulu", "Hawaii Time (HST, UTC-10)"],
      ["America/Mexico_City", "Mexico City (CST/CDT, UTC-6/-5)"],
      ["America/Bogota", "Colombia Time (COT, UTC-5)"],
      ["America/Sao_Paulo", "Brazil / São Paulo (BRT/BRST, UTC-3/-2)"],
      ["America/Argentina/Buenos_Aires", "Argentina Time (ART, UTC-3)"],
    ],
  },
];

const ALL_ZONES = TIMEZONE_GROUPS.flatMap(g => g.zones.map(([value]) => value));

// Today's calendar date in the given IANA timezone, as a YYYY-MM-DD string.
// en-CA formats natively as YYYY-MM-DD, so no manual assembly is needed.
function todayInZone(timeZone) {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat("en-CA").format(new Date());
  }
}

export default function PasskeyGenerator() {
  const api = useApi();

  // Detect the browser's timezone; fall back to UTC if it's not one we list.
  const detectedZone = useMemo(() => {
    const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ALL_ZONES.includes(guess) ? guess : "UTC";
  }, []);

  const [timezone, setTimezone] = useState(detectedZone);
  const [date, setDate] = useState(() => todayInZone(detectedZone));
  const [passkey, setPasskey] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // On mount, ensure the date reflects the detected zone.
  useEffect(() => {
    setDate(todayInZone(detectedZone));
  }, [detectedZone]);

  const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(date);

  const handleTimezoneChange = value => {
    setTimezone(value);
    // Auto-fill the date with "today" in the newly selected zone. The DA can override.
    setDate(todayInZone(value));
    setPasskey(null);
    setError(null);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setPasskey(null);
    try {
      const utcDate = getUTCDateForLocalMidnight(date, timezone);
      const result = await api.generatePasskey(utcDate);
      if (result?.error) {
        setError(result.error);
      } else {
        setPasskey(result.passkey);
      }
    } catch (e) {
      setError("Could not generate a passkey. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Fragment>
      <H2 className={pageTitle}>Passkey Generator</H2>

      <Body>Generate unique passkey for MDB Gen AI Dev Days</Body>

      <div className={formGroup}>
        <Select
          label="Timezone"
          value={timezone}
          onChange={handleTimezoneChange}
          allowDeselect={false}
        >
          {TIMEZONE_GROUPS.map(group => (
            <OptionGroup key={group.label} label={group.label}>
              {group.zones.map(([value, display]) => (
                <Option key={value} value={value}>{display}</Option>
              ))}
            </OptionGroup>
          ))}
        </Select>
      </div>

      <div className={formGroup}>
        <label className={dateLabel} htmlFor="passkey-date">Event Date</label>
        <input
          id="passkey-date"
          type="date"
          className={dateInput}
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>

      <div className={formGroup}>
        <Button
          variant="primary"
          type="button"
          disabled={!isValidFormat || loading}
          onClick={handleGenerate}
        >
          Generate Passkey
        </Button>
      </div>

      {passkey && (
        <div className={resultPanel}>
          <Copyable label={`Passkey for ${date}`}>{passkey}</Copyable>
        </div>
      )}

      {error && (
        <div className={css`${spaced}; max-width: 440px;`}>
          <Banner variant="danger">{error}</Banner>
        </div>
      )}
    </Fragment>
  );
}
